import { type VersionToGatekeeper } from '@casumo/fabric-fundamentals';
import type { TActiveRoute } from '../routing/types';
import { compile, match } from 'path-to-regexp';
import { sourcedBus } from './MessageBus';

type ExtractGatekeeperSnapshot<T> = {
    // ESLINT, Respectfully, i know what i am doing!
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof T]: T[K] extends (...args: any[]) => unknown
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          T[K] extends (...args: any[]) => Promise<infer R>
            ? R extends void
                ? undefined
                : R
            : ReturnType<T[K]> extends object
              ? ExtractGatekeeperSnapshot<ReturnType<T[K]>>
              : ReturnType<T[K]> extends void
                ? undefined
                : ReturnType<T[K]>
        : T[K] extends object
          ? ExtractGatekeeperSnapshot<T[K]>
          : T[K];
};

type TGenericGatekeeper = VersionToGatekeeper['v2.2.13'];
type TGatekeeperCompleteSnapshot = ExtractGatekeeperSnapshot<TGenericGatekeeper>;
export type TEnvContext = {
    route: TActiveRoute;
    countryCodeByIp: string | null;
    operationalNavigationSlugs: Record<string, string>;
    url: string;
};

type TGatekeeperSnapshotCurated = {
    auth: Omit<TGatekeeperCompleteSnapshot['auth'], 'logIn' | 'logOut' | 'getAccessToken'>;
    localisation: Omit<TGatekeeperCompleteSnapshot['localisation'], 'userTimeZone'>;
    navigation: Pick<TGatekeeperCompleteSnapshot['navigation'], 'getCurrentRoute'>;
    mode: TGatekeeperCompleteSnapshot['mode'];
    isNative: boolean; // custom scenario -- will throw if requestes with getParams
    wallet: TGatekeeperCompleteSnapshot['wallet'];
    usesFabricLayout: TGatekeeperCompleteSnapshot['usesFabricLayout'];
    deviceInfo: TGatekeeperCompleteSnapshot['deviceInfo'];
    context: TEnvContext;
};

const throwableVoidFunction = () => {
    throw new Error(
        'Function or property requested is not available. You might be running Gatekeeper on server and context is insufficient'
    );
};

export class GatekeeperUtils {
    static readonly getRouteById = (
        routeId: string,
        params: Record<string, string> = {},
        slugs: Record<string, string> = {}
    ) => {
        // There is a rather rare scenario page would error, then data.navigationSlugs would be undefined
        const route: string | undefined = slugs[routeId];
        if (!route) return null;
        try {
            return compile(route, { encode: encodeURIComponent })(params);
        } catch {
            return null;
        }
    };
    static readonly getRouteFromPathname = (
        pathname: string,
        navigationSlugs: Record<string, string>
    ): {
        page: string;
        params: Record<string, string>;
    } => {
        let result = { page: '', params: {} };
        for (const routeId in navigationSlugs) {
            const route = navigationSlugs?.[routeId];
            const matcher = match(route, { decode: decodeURIComponent });
            const matchMaybe = matcher(pathname);

            if (matchMaybe) {
                result = {
                    page: routeId,
                    params: (matchMaybe?.params as Record<string, string>) ?? {}
                };
                break;
            }
        }

        return result;
    };
    static readonly compareRoutes = (first: string | null = '', second: string | null = '') => {
        if (!first || !second) return false;
        first = first.endsWith('/') ? first.slice(0, -1) : first;
        second = second.endsWith('/') ? second.slice(0, -1) : second;
        return first === second;
    };
    static readonly isServerMode = () => typeof window === 'undefined';
    static readonly getRouteFromUrl = (url: URL, slugs: Record<string, string>) => ({
        ...GatekeeperUtils.getRouteFromPathname(url.pathname, slugs),
        hash: url.hash,
        query: url.searchParams.toString(),
        href: url.href,
        pathname: url.pathname
    });
    static readonly parseRawSnaspshot = (snapshotRaw: string): TGatekeeperSnapshotCurated => {
        try {
            const parsed = JSON.parse(snapshotRaw) as TGatekeeperSnapshotCurated;
            const requiredKeys = [
                'auth',
                'localisation',
                'navigation',
                'deviceInfo',
                'mode',
                'isNative',
                'wallet',
                'context'
            ];
            if (requiredKeys.every((rK) => rK in parsed)) {
                throw new Error('Invalid snapshot. Some required keys are missing');
            }
            return parsed;
        } catch (e) {
            throw new Error('Error parsing input for Gatekeeper', { cause: e });
        }
    };
    static readonly gatherSnapshotFromObject = async <T extends Record<string, unknown>>(
        obj: T
    ): Promise<ExtractGatekeeperSnapshot<T>> => {
        const current: Partial<ExtractGatekeeperSnapshot<T>> = {};
        const promises: Array<Promise<[keyof T, unknown]>> = [];

        for (const key of Object.keys(obj) as Array<keyof T>) {
            const value = obj[key];
            if (value == null) continue;

            if (typeof value === 'function') {
                promises.push(
                    (async (): Promise<[keyof T, unknown]> => {
                        try {
                            // Call the function and await its result (works for both sync and async)
                            const result = await (value as () => Promise<unknown>)();
                            return [key, result];
                        } catch (e) {
                            console.error(`Error getting key ${String(key)}`, e);
                            return [key, undefined];
                        }
                    })()
                );
            } else if (typeof value === 'object' && !Array.isArray(value)) {
                throw new Error('Not supported yet!');
            } else {
                current[key] = value as ExtractGatekeeperSnapshot<T>[keyof T];
            }
        }

        const resolvedPromises = await Promise.all(promises.filter(Boolean));
        for (const [key, value] of resolvedPromises) {
            current[key] = value as ExtractGatekeeperSnapshot<T>[keyof T];
        }

        return current as ExtractGatekeeperSnapshot<T>;
    };
}

export class Gatekeeper implements TGenericGatekeeper {
    static fromSnapshot(input: TGatekeeperSnapshotCurated): Gatekeeper {
        const staticImplementation: TGenericGatekeeper = {
            auth: {
                getAccessToken: throwableVoidFunction,
                logIn: throwableVoidFunction,
                logOut: throwableVoidFunction,
                getCasumoName: async () => input.auth.getCasumoName,
                getName: async () => input.auth.getName,
                getPlayerEmail: async () => input.auth.getPlayerEmail,
                getPlayerId: async () => input.auth.getPlayerId,
                getSessionId: async () => input.auth.getSessionId,
                isLoggedIn: async () => input.auth.isLoggedIn
            },
            cache: {
                getCache: throwableVoidFunction,
                invalidateAll: throwableVoidFunction,
                invalidateByKey: throwableVoidFunction,
                invalidateByTag: throwableVoidFunction
            },
            ui: {
                disableScroll: throwableVoidFunction,
                enableScroll: throwableVoidFunction,
                get isFullScreen() {
                    throwableVoidFunction();
                    return false;
                },
                toggleFullScreen: throwableVoidFunction
            },
            messageBus: sourcedBus('MOCK_GATEKEEPER'),
            deviceInfo: {
                deviceId: input.deviceInfo.deviceId
            },
            fetch: globalThis.fetch,
            localisation: {
                country: input.localisation.country,
                jurisdiction: input.localisation.jurisdiction,
                localeCode: input.localisation.localeCode,
                urlPrefix: input.localisation.urlPrefix,
                marketSlug: input.localisation.marketSlug,
                get userTimeZone() {
                    throwableVoidFunction();
                    return '';
                },
                systemMarketSlug: input.localisation.systemMarketSlug
            },
            isNative: ((withProps?: boolean) => {
                if (withProps) throwableVoidFunction();
                return input.isNative;
            }) as TGenericGatekeeper['isNative'],
            mode: input.mode,
            navigation: {
                getPreviousUrl: throwableVoidFunction,
                goBack: throwableVoidFunction,
                navigate: throwableVoidFunction,
                getCurrentRoute: () => input.navigation.getCurrentRoute,
                isActiveRoute: (routeId: string, params: Record<string, string> = {}) => {
                    if (!input.context.url) return false;
                    const operationalSlugs = input.context.operationalNavigationSlugs;
                    const lookingFor = GatekeeperUtils.getRouteById(
                        routeId,
                        params,
                        operationalSlugs
                    );
                    try {
                        const currentPath = new URL(input.context.url).pathname;
                        return GatekeeperUtils.compareRoutes(lookingFor, currentPath);
                    } catch {
                        return false;
                    }
                },
                getRoute: (routeId: string, params?: Record<string, string>) => {
                    return GatekeeperUtils.getRouteById(
                        routeId,
                        params,
                        input.context.operationalNavigationSlugs
                    );
                }
            },
            middleware: {
                add: throwableVoidFunction,
                remove: throwableVoidFunction
            },
            wallet: {
                getPlayerCurrency: async () => input.wallet.getPlayerCurrency
            },
            usesFabricLayout: input.usesFabricLayout
        };
        return new Gatekeeper(staticImplementation, input.context);
    }
    static fromRawSnapshot(snapshotRaw: string): Gatekeeper {
        const snapshot = GatekeeperUtils.parseRawSnaspshot(snapshotRaw);
        return Gatekeeper.fromSnapshot(snapshot);
    }

    // GK Instance
    #snapshotPromise: Promise<string> | null = null;
    #context: TEnvContext;
    constructor(implementation: TGenericGatekeeper, envContext: TEnvContext) {
        this.#context = envContext;
        this.auth = implementation.auth;
        this.messageBus = implementation.messageBus;
        this.localisation = implementation.localisation;
        this.navigation = implementation.navigation;
        this.fetch = implementation.fetch;
        this.cache = implementation.cache;
        this.deviceInfo = implementation.deviceInfo;
        this.mode = implementation.mode;
        this.usesFabricLayout = implementation.usesFabricLayout;
        this.wallet = implementation.wallet;
        this.isNative = implementation.isNative;
        this.ui = implementation.ui;
        this.middleware = implementation.middleware;
    }
    public async getGatekeeperSnapshot(): Promise<string> {
        if (this.#snapshotPromise) return this.#snapshotPromise;
        const { promise, resolve } = Promise.withResolvers<string>();
        this.#snapshotPromise = promise;
        const isServer = GatekeeperUtils.isServerMode();

        const { logIn: _, logOut: __, getAccessToken: _gAT, ...essentialAuth } = this.auth;
        const navSlimVersion = {
            getCurrentRoute: this.navigation.getCurrentRoute,
            ...(isServer ? {} : { getPreviousUrl: this.navigation.getPreviousUrl })
        };

        let localisation;
        if (GatekeeperUtils.isServerMode()) {
            // slim version -- no extraction of get userTimeZone
            const { country, jurisdiction, localeCode, marketSlug, urlPrefix, systemMarketSlug } =
                this.localisation;
            localisation = {
                country,
                jurisdiction,
                localeCode,
                marketSlug,
                urlPrefix,
                systemMarketSlug
            };
        } else {
            //full version
            localisation = { ...this.localisation };
        }
        Promise.all([
            GatekeeperUtils.gatherSnapshotFromObject<typeof essentialAuth>(essentialAuth),
            GatekeeperUtils.gatherSnapshotFromObject<typeof navSlimVersion>(navSlimVersion),
            GatekeeperUtils.gatherSnapshotFromObject(this.wallet)
        ]).then(([auth, navigation, wallet]) => {
            const currentSnapshot: TGatekeeperSnapshotCurated = {
                auth,
                localisation,
                navigation,
                deviceInfo: { ...this.deviceInfo },
                mode: this.mode,
                isNative: this.isNative(),
                wallet,
                usesFabricLayout: this.usesFabricLayout,
                context: this.#context
            };
            resolve(JSON.stringify(currentSnapshot));
        });

        // server side, promise is resolved once per request
        // and all the calls to getGatekeeperSnapshot will return the same promise
        // --
        // on browser, everybody calling the function while it's
        // "resolving" receives the same snapshot
        if (!isServer)
            promise.finally(() => {
                this.#snapshotPromise = null;
            });
        return promise;
    }
    public auth: TGenericGatekeeper['auth'];
    public messageBus: TGenericGatekeeper['messageBus'];
    public localisation: TGenericGatekeeper['localisation'];
    public navigation: TGenericGatekeeper['navigation'];
    public fetch: TGenericGatekeeper['fetch'];
    public cache: TGenericGatekeeper['cache'];
    public deviceInfo: TGenericGatekeeper['deviceInfo'];
    public mode: TGenericGatekeeper['mode'];
    public usesFabricLayout: TGenericGatekeeper['usesFabricLayout'];
    public wallet: TGenericGatekeeper['wallet'];
    public isNative: TGenericGatekeeper['isNative'];
    public ui: TGenericGatekeeper['ui'];
    public middleware: TGenericGatekeeper['middleware'];
}
